<script setup lang="ts">
import { T } from '@tolgee/vue'
import { ref, watch } from 'vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import type { EggGameCardCharacter } from '@/partners/lingo/lingo-giveaway/types/egg-game'
import BaseButton from '@/components/base/BaseButton.vue'
import TwitterIcon from '@/components/icons/TwitterIcon.vue'
import { twitterPostLink } from '@/config/twitter'
import webapp from '@/services/webapp'
import { goldCharacterId } from '@/partners/lingo/lingo-giveaway/config/characters'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { winPrize } from '@/partners/lingo/lingo-giveaway/composables/lingo-giveaway'

const props = defineProps<{
  show: boolean,
  content: EggGameCardCharacter,
}>()

const emit = defineEmits<{
  (e: 'ctaClick'): void
}>()

const isClickable = ref(false)

function shareOnX(e: MouseEvent) {
  e.preventDefault()
  webapp.openLink(twitterPostLink + `?type=lingoGachaMaverickCollected`)
}

function onContainerClick() {
  if (isClickable.value) {
    emit('ctaClick')
  }
}

watch(() => props.show, show => {
  if (show) {
    document.body.classList.add('!overflow-hidden')
    setTimeout(() => isClickable.value = true, 300)
  } else {
    document.body.classList.remove('!overflow-hidden')
    isClickable.value = false
  }
}, { immediate: true })

</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 bg-dark-most-bottom-layer/[.65] backdrop-blur-[4px] flex flex-col z-[800]"
      @click="onContainerClick"
    >
      <CrossCircleIcon class="w-6 h-6 min-w-[24px] absolute top-3 right-3 text-white/[87%]" />
      <div class="h-full w-full flex-1 flex flex-col gap-y-8 items-center justify-center relative">
        <div
          class="absolute w-[334px] h-[334px] rounded-full bg-green-primary/[.54] blur-[150px] -z-[1]"
        />
        <div class="flex flex-col w-full gap-y-3 justify-center items-center px-[28.5px]">
          <div class="flex flex-col items-center">
            <div class="text-white text-[24px] font-bold leading-6 whitespace-pre-line text-center">
              Unlocked Maverick {{ content.index + 1 }}
            </div>
            <div class="uppercase text-center text-white text-[36px] leading-[42px] whitespace-pre-line font-bold">
              {{ content.name }}
            </div>
          </div>
        </div>

        <img
          :src="content.image"
          class="h-[212px] w-[144px] object-cover"
        >

        <p class="whitespace-pre-line text-center text-base font-medium tracking-tight text-solid-white">
          <template v-if="content.id === goldCharacterId">
            Congrats! You’ve won a share on the
            ${{ formatNumberFullTruncate(winPrize) }} prize!
          </template>
          <template v-else>
            Collect more Mavericks to unlock <br> your share of the ${{ formatNumberFullTruncate(winPrize) }}
            prize!
          </template>
        </p>

        <BaseButton
          class="w-full max-w-[250px]"
          @click="shareOnX"
        >
          <template #leftIcon>
            <TwitterIcon class="w-6 h-6 min-w-6" />
          </template>
          <T
            key-name="lingo.giveaway.overlay.cta"
            default-value="SHARE ON X"
          />
        </BaseButton>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to /* .fade-leave-active in <2.1.8 */
{
  opacity: 0;
}

.golden-border {
  background: linear-gradient(90deg, #FFCB00 0%, #B57800 22%, #B77C07 26%, #BF881B 31%, #CB9D3C 37%, #DCB96A 44%, #F1DDA5 52%, #FFF4C9 56%, #EACE88 62%, #D8AD4E 68%, #CB9423 73%, #C38509 77%, #C08000 79%, #C58500 81%, #EAB200 94%, #F9C300 100%);
}
</style>